import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, X, Bell, Trash2, CheckCircle, AlertCircle, Clock, Info,
  RefreshCw, Package, Truck, Factory, Tag, Filter, Zap
} from 'lucide-react';
import { dismissAllSystemNotifications, dismissSystemNotifications, syncBadgeFromServer } from '../lib/notifyBridge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_STYLES = {
  alert:    { bg: 'bg-red-900/20 border-red-700/50',    icon: AlertCircle, color: 'text-red-400',    badge: 'bg-red-900/40 text-red-300 border-red-700' },
  reminder: { bg: 'bg-amber-900/20 border-amber-700/50', icon: Clock,        color: 'text-amber-400',  badge: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  notice:   { bg: 'bg-blue-900/20 border-blue-700/50',   icon: Info,         color: 'text-blue-400',   badge: 'bg-blue-900/40 text-blue-300 border-blue-700' },
  todo:     { bg: 'bg-green-900/20 border-green-700/50', icon: CheckCircle,  color: 'text-green-400',  badge: 'bg-green-900/40 text-green-300 border-green-700' },
};

const PRIORITY_BADGE = {
  urgent: 'bg-red-900/40 text-red-300 border-red-700',
  high:   'bg-orange-900/40 text-orange-300 border-orange-700',
  normal: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  low:    'bg-zinc-900 text-zinc-600 border-zinc-800',
};

const TAG_COLOR_MAP = {
  material_inward: 'bg-blue-900/40 text-blue-300 border-blue-700/50',
  fg_outward:      'bg-green-900/40 text-green-300 border-green-700/50',
  dispatched:      'bg-teal-900/40 text-teal-300 border-teal-700/50',
  production_update: 'bg-purple-900/40 text-purple-300 border-purple-700/50',
  inventory:       'bg-blue-900/30 text-blue-400 border-blue-800/50',
  sales:           'bg-amber-900/40 text-amber-300 border-amber-700/50',
  po_received:     'bg-cyan-900/40 text-cyan-300 border-cyan-700/50',
  completed:       'bg-green-900/40 text-green-300 border-green-700/50',
  in_production:   'bg-violet-900/40 text-violet-300 border-violet-700/50',
};

const AUTO_ICONS = {
  inventory: Package,
  sales: Truck,
  production: Factory,
};

const emptyForm = {
  type: 'notice', title: '', message: '', priority: 'normal',
  module: 'general', is_todo: false, due_date: '', tags: '',
};

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('All');
  const [tagFilter, setTagFilter] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchItems();
    // Opening the in-app feed counts as "viewing" all current alerts —
    // close every Tirth ERP system-tray banner so the OS notification centre
    // mirrors the in-app state, then refresh the OS app-icon badge from the
    // server (which only counts truly-unread items).
    dismissAllSystemNotifications();
    syncBadgeFromServer();
  }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      // Best-effort: dismiss the matching system tray banner too. We tag
      // banners by `event_key` (set on the FCM payload), so finding the
      // right banner is easy when the user reads a single notification.
      const item = items.find(n => n.id === id);
      if (item?.event_key) dismissSystemNotifications({ tag: item.event_key });
      // Re-sync OS badge so the counter goes down with the unread total.
      syncBadgeFromServer();
    } catch (e) { console.error(e); }
  };

  const markComplete = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/complete`, {}, { withCredentials: true });
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_completed: true } : n));
      toast.success('Task marked complete!');
    } catch (e) { console.error(e); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
      setItems(prev => prev.filter(n => n.id !== id));
    } catch (e) { toast.error('Error deleting notification'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim().toLowerCase().replace(/\s+/g, '_')).filter(Boolean) : [],
      };
      const res = await axios.post(`${API}/notifications/`, payload, { withCredentials: true });
      setItems(prev => [res.data, ...prev]);
      setShowModal(false);
      setForm(emptyForm);
      toast.success('Notification created!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const unreadCount = items.filter(n => !n.is_read && !n.is_auto).length;
  const autoItems = items.filter(n => n.is_auto);
  const manualItems = items.filter(n => !n.is_auto);

  // All unique tags across all items
  const allTags = [...new Set(items.flatMap(n => n.tags || []))].sort();

  const filtered = items.filter(n => {
    if (filter === 'Unread') { if (n.is_read) return false; }
    else if (filter === 'Alerts') { if (n.type !== 'alert') return false; }
    else if (filter === 'Todos') { if (!n.is_todo) return false; }
    else if (filter === 'Auto') { if (!n.is_auto) return false; }
    else if (filter === 'Todos Pending') { if (!n.is_todo || n.is_completed) return false; }
    if (tagFilter && !(n.tags || []).includes(tagFilter)) return false;
    return true;
  });

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse p-8">Loading notifications...</div>;

  return (
    <div className="space-y-5" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Alerts & Notifications</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            {unreadCount > 0 ? <span className="text-amber-400 font-medium">{unreadCount} unread</span> : 'All caught up'}
            {' · '}{autoItems.length} system · {manualItems.length} manual
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchItems} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800 transition-colors"><RefreshCw size={14} /></button>
          <button data-testid="add-notification-btn" onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> Add Notice
          </button>
        </div>
      </div>

      {/* Auto-Activity Feed — pinned at top */}
      {autoItems.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={13} className="text-amber-400" />
            <span className="text-zinc-300 text-sm font-semibold">Live Activity Feed</span>
            <span className="text-xs text-zinc-600">(system auto-generated)</span>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {autoItems.slice(0, 8).map(n => {
              const AutoIcon = AUTO_ICONS[n.module] || Zap;
              const moduleColor = n.module === 'inventory' ? 'text-blue-400 bg-blue-900/20' : n.module === 'sales' ? 'text-green-400 bg-green-900/20' : 'text-purple-400 bg-purple-900/20';
              return (
                <div key={n.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-zinc-800/40 border border-zinc-700/40">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${moduleColor}`}>
                    <AutoIcon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-zinc-200 text-xs font-medium truncate">{n.title}</span>
                      <span className="text-zinc-600 text-xs flex-shrink-0">{fmtTime(n.created_at)}</span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5 line-clamp-1">{n.message}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(n.tags || []).map(tag => (
                        <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium cursor-pointer ${TAG_COLOR_MAP[tag] || 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}
                          onClick={() => setTagFilter(tag === tagFilter ? '' : tag)}>
                          {tag.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          {['All', 'Unread', 'Todos', 'Todos Pending', 'Alerts', 'Auto'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`notif-filter-${f.toLowerCase().replace(' ', '-')}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${filter === f ? 'bg-amber-500 text-black border-amber-500' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
              {f}{f === 'Unread' && unreadCount > 0 ? ` (${unreadCount})` : ''}
              {f === 'Auto' && autoItems.length > 0 ? ` (${autoItems.length})` : ''}
            </button>
          ))}
        </div>

        {/* Tag Filter */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Tag size={11} className="text-zinc-600" />
            {allTags.map(tag => (
              <button key={tag} onClick={() => setTagFilter(tag === tagFilter ? '' : tag)}
                className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${tagFilter === tag ? (TAG_COLOR_MAP[tag] || 'bg-amber-900/40 text-amber-300 border-amber-700') + ' font-semibold' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>
                {tag.replace(/_/g, ' ')}
              </button>
            ))}
            {tagFilter && <button onClick={() => setTagFilter('')} className="text-[10px] text-zinc-600 hover:text-zinc-300 flex items-center gap-0.5"><X size={10} /> Clear</button>}
          </div>
        )}
      </div>

      {/* Notification List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
            <Bell size={28} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">No notifications in this category</p>
          </div>
        )}
        {filtered.map(n => {
          const style = TYPE_STYLES[n.type] || TYPE_STYLES.notice;
          const Icon = n.is_auto ? (AUTO_ICONS[n.module] || Zap) : style.icon;
          return (
            <div key={n.id} data-testid={`notif-${n.id}`}
              onClick={() => !n.is_read && markRead(n.id)}
              className={`border rounded-xl p-4 transition-all ${n.is_read ? 'bg-zinc-900 border-zinc-800' : `${style.bg} border`} ${n.is_completed ? 'opacity-50' : ''} ${!n.is_read ? 'cursor-pointer hover:opacity-90' : ''}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${n.is_read ? 'bg-zinc-800' : 'bg-zinc-800/80'}`}>
                  <Icon size={15} className={n.is_read ? 'text-zinc-500' : (n.is_auto ? 'text-amber-400' : style.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-sm font-medium ${n.is_read ? 'text-zinc-400' : 'text-zinc-50'}`}>{n.title}</span>
                      {!n.is_read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />}
                      {n.is_auto && <span className="text-[9px] bg-amber-900/40 text-amber-400 border border-amber-800/50 px-1.5 py-0.5 rounded-full">AUTO</span>}
                      {/* "BY YOU" badge — confirms the entry is the actor's own
                          activity (server pre-marks it read so it doesn't bump
                          the unread count). Otherwise users wonder why their
                          recent action seems to have produced no log entry. */}
                      {n.is_self && <span className="text-[9px] bg-blue-900/40 text-blue-300 border border-blue-800/50 px-1.5 py-0.5 rounded-full">BY YOU</span>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-zinc-600 text-xs">{fmtTime(n.created_at)}</span>
                      {n.is_todo && !n.is_completed && (
                        <button onClick={e => { e.stopPropagation(); markComplete(n.id); }}
                          className="text-zinc-600 hover:text-green-400 transition-colors" title="Mark complete" data-testid={`complete-${n.id}`}>
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {!n.is_auto && (
                        <button onClick={e => { e.stopPropagation(); deleteItem(n.id); }}
                          className="text-zinc-700 hover:text-red-400 transition-colors" data-testid={`delete-${n.id}`}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className={`text-xs mt-0.5 ${n.is_read ? 'text-zinc-600' : 'text-zinc-400'}`}>{n.message}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${style.badge}`}>{n.type}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${PRIORITY_BADGE[n.priority] || PRIORITY_BADGE.normal}`}>{n.priority}</span>
                    {n.module && n.module !== 'general' && (
                      <span className="text-[10px] text-zinc-600 capitalize">{n.module}</span>
                    )}
                    {(n.tags || []).map(tag => (
                      <button key={tag} onClick={e => { e.stopPropagation(); setTagFilter(tag === tagFilter ? '' : tag); }}
                        className={`text-[9px] px-1.5 py-0.5 rounded-full border transition-colors ${TAG_COLOR_MAP[tag] || 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}>
                        {tag.replace(/_/g, ' ')}
                      </button>
                    ))}
                    {n.is_completed && <span className="text-[10px] text-green-600 font-medium">Completed</span>}
                    {n.due_date && !n.is_completed && (
                      <span className={`text-[10px] font-mono ${new Date(n.due_date) < new Date() ? 'text-red-400' : 'text-zinc-500'}`}>
                        Due: {n.due_date?.slice(0, 10)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md my-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">Add Notice / Todo</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="notification-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['notice', 'alert', 'reminder', 'todo'].map(t => <option key={t} value={t} className="bg-zinc-900 capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['urgent', 'high', 'normal', 'low'].map(p => <option key={p} value={p} className="bg-zinc-900 capitalize">{p}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Title *</label>
                  <input required data-testid="notif-title-input" value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Short title..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Message</label>
                  <textarea rows={2} value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Details..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 resize-none" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Module</label>
                  <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['general', 'sales', 'inventory', 'production', 'purchases', 'samples', 'compliance', 'cashflow'].map(m => <option key={m} value={m} className="bg-zinc-900 capitalize">{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Due Date</label>
                  <input type="date" value={form.due_date}
                    onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 flex items-center gap-1"><Tag size={11} /> Tags <span className="text-zinc-600">(comma-separated, e.g. urgent, quality, dispatch)</span></label>
                  <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="e.g. quality_check, raw_material, dispatch" data-testid="notif-tags-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_todo}
                      onChange={e => setForm(f => ({ ...f, is_todo: e.target.checked, type: e.target.checked ? 'todo' : 'notice' }))}
                      className="accent-amber-500 w-4 h-4" />
                    <span className="text-zinc-400 text-xs">Mark as Todo (track completion)</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button data-testid="submit-notif-btn" type="submit" disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : 'Add Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
